import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { priceFormat } from 'helpers/utils/priceConverter';
import { useAccount, useProduct } from 'frontastic';
import { useFormat } from './useFormat';

type UseFetchAndModifyProductsProps = {
  selector: string;
  onTriggerLogin: () => void;
};
interface Promotion {
  name: string;
}

interface ProductPromotion {
  skuId: string;
  productId: string;
  promotions: Promotion[];
}

export const useFetchAndModifyProducts = ({ selector, onTriggerLogin }: UseFetchAndModifyProductsProps) => {
  const requestedSkus = useRef(new Set<string>());
  const { getDroplets, getProductsWithPrices } = useProduct();
  const { loggedIn } = useAccount();
  const observerRef = useRef<MutationObserver | null>(null);
  const router = useRouter();
  const retryRef = useRef(0);
  const maxRetries = 10;
  // Retry logic with optional delay
  const tryHandle = async (label: string) => {
    const items = document.querySelectorAll(selector);
    if (items.length > 0) {
      await fetchAndModifyProducts();
      observerRef.current?.disconnect();
    } else if (retryRef.current < maxRetries) {
      retryRef.current++;
      setTimeout(() => tryHandle(label), 400);
    } else {
      // console.warn(`❌ ${label} max retries reached`);
    }
  };

  // Main function to run the modifier logic.
  const runModifier = () => {
    retryRef.current = 0;
    const label = loggedIn ? 'LoggedIn' : 'LoggedOut';
    tryHandle(label);
    // Set up an observer to re-try if products are added/modified to the DOM.
    observerRef.current?.disconnect();
    observerRef.current = new MutationObserver(() => tryHandle(label));
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  // Initial and route-change load
  useEffect(() => {
    const handleRouteChange = () => {
      if (!selector) {
        observerRef.current?.disconnect();
        return;
      }
      requestedSkus.current.clear();
      setTimeout(() => {
        runModifier();
      }, 2000);
    };

    // Run modifier logic on initial load
    setTimeout(() => {
      runModifier();
    }, 2000);

    // Listen for route changes
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      observerRef.current?.disconnect();
    };
  }, [selector, router?.asPath, loggedIn]);

  const extractSKUs = (productItems) => {
    return [...productItems].map((item) => item.dataset.pid).filter(Boolean);
  };
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  // Pricing calculations
  const calculatePriceDetails = (product) => {
    const sku = product?.variants?.[0];
    const listPrice = sku?.listPrice;
    const salePrice = sku?.salePrice;
    const msrp = sku?.msrp;
    const msrpValues = product?.variants?.map((variant) => variant?.msrp || null);
    const filteredMsrpValues = msrpValues?.filter((value) => typeof value === 'number');
    const uniqueMsrpValues: number[] = Array.from(new Set(filteredMsrpValues));

    const listPriceValues = product?.variants?.map((variant) => variant?.listPrice || null);
    const filteredListPriceValues = listPriceValues?.filter((value) => typeof value === 'number');
    const uniqueListPriceValues: number[] = Array.from(new Set(filteredListPriceValues));

    const salePriceValues = product?.variants?.map((variant) => variant?.salePrice || null);
    const filteredSalePriceValues = salePriceValues?.filter((value) => typeof value === 'number');
    const uniqueSalePriceValues: number[] = Array.from(new Set(filteredSalePriceValues));

    const priceRangeMaxMSRP =
      product?.variants?.length === 1
        ? msrp ?? undefined
        : uniqueMsrpValues?.length > 0
        ? Math.max(...uniqueMsrpValues)
        : undefined;
    const priceRangeMaxListPrice =
      product?.variants?.length === 1
        ? listPrice ?? undefined
        : uniqueListPriceValues?.length > 0
        ? Math.max(...uniqueListPriceValues)
        : undefined;
    const priceRangeMaxSalePrice =
      product?.variants?.length === 1
        ? salePrice ?? undefined
        : uniqueSalePriceValues?.length > 0
        ? uniqueListPriceValues?.length === 1 &&
          uniqueListPriceValues?.[0] > Math.min(...uniqueSalePriceValues) &&
          uniqueListPriceValues?.[0] <= Math.max(...uniqueSalePriceValues) &&
          !priceRangeMaxMSRP
          ? Math.min(...uniqueSalePriceValues)
          : Math.max(...uniqueSalePriceValues)
        : undefined;

    const isListPriceLarger = (() => {
      if (!priceRangeMaxSalePrice) return true;
      if (product?.variants?.length === 1) return listPrice > salePrice;
      if (
        uniqueListPriceValues?.length === 1 &&
        uniqueSalePriceValues?.length > 1 &&
        uniqueListPriceValues?.[0] >= Math.max(...uniqueSalePriceValues)
      )
        return uniqueListPriceValues?.[0] > Math.min(...uniqueSalePriceValues);
      else {
        return priceRangeMaxListPrice > priceRangeMaxSalePrice;
      }
    })();

    const calculateMoneyStats = (moneyArray) => {
      const filteredMoneyArray = moneyArray
        ?.filter((money) => money !== undefined)
        ?.sort((a, b) => (a || 0) - (b || 0));
      const lowest = filteredMoneyArray?.[0];
      const highest = filteredMoneyArray?.[filteredMoneyArray?.length - 1];
      const difference =
        lowest && highest
          ? {
              centAmount: (highest || 0) - (lowest || 0),
            }
          : undefined;
      const percentageDifference = difference ? (difference?.centAmount / (highest || 1)) * 100 : 0;
      return {
        difference,
        percentageDifference: Math.round(percentageDifference),
        filteredMoneyArray,
      };
    };

    const displayMsrpRange = (() => {
      if (product?.variants?.length > 1) {
        if (uniqueMsrpValues?.length > 1) {
          const minMsrp = Math.min(...uniqueMsrpValues);
          const maxMsrp = Math.max(...uniqueMsrpValues);
          return `${priceFormat(minMsrp)} - ${priceFormat(maxMsrp)}`;
        } else if (uniqueMsrpValues?.length === 1) {
          const uniqueMsrpPrice = uniqueMsrpValues?.[0];
          return priceFormat(uniqueMsrpPrice);
        }
      }
      return priceFormat(msrp);
    })();

    const displayListPriceRange = (() => {
      const storePrice = product?.storePrice;
      const isUsingFacilityBasedPrice = product?.useFacilityBasedPrice;

      if (isUsingFacilityBasedPrice) {
        // If using facility-based price, check if storePrice exists
        return priceFormat(storePrice) || '';
      } else {
        // If not using facility-based price, fallback to list price or list price range
        if (product?.variants?.length > 1) {
          if (uniqueListPriceValues?.length > 1) {
            const minListPrice = Math.min(...uniqueListPriceValues);
            const maxListPrice = Math.max(...uniqueListPriceValues);
            return `${priceFormat(minListPrice)} - ${priceFormat(maxListPrice)}`;
          } else if (uniqueListPriceValues?.length === 1) {
            const uniqueListPrice = uniqueListPriceValues?.[0];
            return priceFormat(uniqueListPrice);
          }
        }
        return priceFormat(listPrice);
      }
    })();

    const displaySalePriceRange = (() => {
      if (product?.variants?.length > 1) {
        if (uniqueSalePriceValues?.length > 1) {
          const minSalePrice = Math.min(...uniqueSalePriceValues);
          const maxSalePrice = Math.max(...uniqueSalePriceValues);
          return isListPriceLarger ? `${priceFormat(minSalePrice)} - ${priceFormat(maxSalePrice)} Sale` : null;
        } else if (uniqueSalePriceValues?.length === 1) {
          const uniqueSalePrice = uniqueSalePriceValues?.[0];
          return isListPriceLarger && uniqueSalePrice ? `${priceFormat(uniqueSalePrice)} Sale` : null;
        }
      }
      return isListPriceLarger && salePrice ? `${priceFormat(salePrice)} Sale` : null;
    })();

    const moneyArray = [priceRangeMaxMSRP, priceRangeMaxListPrice, priceRangeMaxSalePrice];
    const moneyStats = calculateMoneyStats(moneyArray);

    const isOnlylistPrice = msrp === undefined && !isListPriceLarger && moneyStats?.difference?.centAmount <= 0;

    const saveUpto =
      uniqueListPriceValues?.length > 1 || uniqueMsrpValues?.length > 1 || uniqueSalePriceValues?.length > 1;

    return {
      displayMsrpRange,
      displayListPriceRange,
      displaySalePriceRange,
      moneyStats,
      isOnlylistPrice,
      saveUpto,
    };
  };
  const renderLoginForPrice = () => {
    const loginDiv = document.createElement('div');
    loginDiv.className = 'w-fit cursor-pointer text-sm leading-5 text-blue-900 hover:underline py-1';
    loginDiv.innerHTML = `
      <p>${formatProductMessage({
        id: 'logForPricing',
        defaultMessage: 'Log in for Exchange pricing',
      })}</p>
    `;
    loginDiv.addEventListener('click', () => {
      try {
        onTriggerLogin();
        return;
        //showLogin(); // Call the existing showLogin function
      } catch (error) {
        console.error('showLogin is not defined or failed:', error);
      }
    });
    return loginDiv;
  };

  const prepareDOMContainers = (productItems: NodeListOf<HTMLElement>) => {
    productItems.forEach((productItem) => {
      let priceContainer = productItem.querySelector('.custom-price-info') as HTMLElement;
      let promoContainer = productItem.querySelector('.promo-container') as HTMLElement;

      if (!priceContainer) {
        priceContainer = document.createElement('div');
        priceContainer.className = 'w-full custom-price-info';
        productItem.appendChild(priceContainer);
      }

      if (!promoContainer) {
        promoContainer = document.createElement('div');
        promoContainer.className = 'w-full promo-container';
        if (!priceContainer.parentElement) {
          productItem.appendChild(priceContainer);
        }
        priceContainer.after(promoContainer); // Ensure promo follows price
      }
    });
  };

  const attachPriceToDOM = (productIds, productData) => {
    setTimeout(() => {
      productIds.forEach((productId) => {
        const productItems = document.querySelectorAll(`${selector}[data-pid="${productId}"]`);

        productItems.forEach((productItem) => {
          const href = productItem.getAttribute('data-pid');
          const productRecord = productData.records.find((record) => href === record.productId);

          if (!productRecord) {
            return;
          }

          let priceContainer = productItem.querySelector('.custom-price-info');
          if (!priceContainer) {
            priceContainer = document.createElement('div');
            priceContainer.className = 'w-full custom-price-info';
            productItem.appendChild(priceContainer);
          }

          const isUseFacilityBasedPrice = productRecord?.useFacilityBasedPrice;
          const isMSRPRestricted = productRecord?.msrpRestriction;

          if ((isUseFacilityBasedPrice || isMSRPRestricted) && !loggedIn) {
            const newNode = renderLoginForPrice();
            const existingNode = priceContainer.firstElementChild as HTMLElement;

            if (!existingNode || existingNode.outerHTML !== newNode.outerHTML) {
              priceContainer.innerHTML = '';
              priceContainer.appendChild(newNode);
            }
            return;
          }

          const { displayListPriceRange, displaySalePriceRange, isOnlylistPrice } =
            calculatePriceDetails(productRecord);

          const priceWrapper = document.createElement('div');
          priceWrapper.className = 'flex flex-wrap gap-2 py-1';

          if (displayListPriceRange) {
            const priceRangeElement = document.createElement('span');
            priceRangeElement.className = `text-sm leading-5 text-gray-900 ${isOnlylistPrice ? 'font-bold' : ''}`;
            priceRangeElement.textContent = displayListPriceRange;
            priceWrapper.appendChild(priceRangeElement);
          }

          if (displaySalePriceRange) {
            const salePriceElement = document.createElement('p');
            salePriceElement.className = 'text-sm font-bold leading-5 text-[#DA0F0F]';
            salePriceElement.textContent = displaySalePriceRange;
            priceWrapper.appendChild(salePriceElement);
          }

          const newHTML = priceWrapper.outerHTML;
          const isEmpty = !priceContainer.children.length;

          if (priceContainer.innerHTML.trim() !== newHTML.trim() || isEmpty) {
            priceContainer.innerHTML = '';
            priceContainer.appendChild(priceWrapper);
          } else {
          } // console.log(`⏩ Skipping update: identical DOM for ${productId}`);
        });
      });
    }, 1000);
  };
  //---end
  const attachPromotionToDOM = (selector: string, skuId: string, productId: string, promotions: Promotion[]) => {
    const applyPromotions = () => {
      const productItems = document.querySelectorAll(`${selector}[data-pid="${productId}"]`);
      productItems.forEach((productItem) => {
        const priceContainer = productItem.querySelector('.custom-price-info');
        let promoContainer = productItem.querySelector('.promo-container') as HTMLElement;

        if (!promoContainer) {
          promoContainer = document.createElement('div');
          promoContainer.className = 'promo-container';
          promoContainer.style.marginTop = '5px';
        } else {
          // Remove from old location
          promoContainer.remove();
        }
        // Always insert after price container
        if (priceContainer) {
          priceContainer.after(promoContainer);
        } else {
          productItem.appendChild(promoContainer); // Fallback
        }

        promoContainer.innerHTML = ''; // Clear previous promos

        promotions?.forEach((promotion) => {
          const promoDiv = document.createElement('div');
          promoDiv.className = 'promo-tag text-[13px] leading-5 text-green-900';
          promoDiv.style.marginBottom = '2px';
          promoDiv.textContent = promotion.name; // Only text, no image
          promoContainer.appendChild(promoDiv);
        });
      });
    };
    setTimeout(() => {
      applyPromotions();
    }, 1100);
  };
  const fetchPLPDroplets = async (productData) => {
    const chunkSize = 32;
    function formatProductForApi(product) {
      const variant = product?.variants?.[0];
      return {
        productId: product?.productId,
        skuId: variant?.skuId,
        activePrice: variant?.activePrice,
      };
    }
    const apiRequestPayload = productData?.map(formatProductForApi);
    const promises = [];
    for (let i = 0; i < apiRequestPayload?.length; i += chunkSize) {
      const chunk = apiRequestPayload.slice(i, i + chunkSize);
      // console.log(`🔁 Calling getDroplets for chunk #${i / chunkSize + 1}`, chunk);
      promises.push(getDroplets(chunk));
    }
    try {
      const allPLPDroplets = await Promise.all(promises)
        .then((results) => results?.flat()?.filter((droplet) => !droplet?.errorCode))
        .catch((error) => {
          console.error('❌ Error fetching PLP Droplets:', error);
          return [];
        });

      const newPromotions: ProductPromotion[] = [];
      allPLPDroplets.forEach((droplet) => {
        if (droplet?.promotions?.length > 0) {
          newPromotions.push({ skuId: droplet?.skuId, productId: droplet?.productId, promotions: droplet?.promotions });
          attachPromotionToDOM(selector, droplet?.skuId, droplet?.productId, droplet?.promotions); // Fallback DOM update
        }
      });
    } catch (error) {
      console.error('❌ Error in processing PLP Droplets:', error);
    }
  };
  const fetchAndModifyProducts = async () => {
    try {
      const productItems = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;
      if (productItems.length === 0) {
        console.warn(`⚠️ No products found for selector: ${selector}`);
        return;
      }

      const skus = extractSKUs(productItems).filter((sku) => {
        if (!sku) console.warn('⚠️ Missing data-pid on productItem:', productItems);
        return !requestedSkus.current.has(sku);
      });

      if (skus.length === 0) return;

      skus.forEach((sku) => requestedSkus.current.add(sku));

      const productData = await getProductsWithPrices(skus.join(','));
      if (!productData?.records?.length) {
        console.warn('⚠️ No product data returned from API');
        return;
      }

      prepareDOMContainers(productItems);
      attachPriceToDOM(skus, productData);
      await fetchPLPDroplets(productData.records);
    } catch (error) {
      console.error('❌ Error fetching or modifying products:', error);
    }
  };
};
